import { createFileRoute } from "@tanstack/react-router";
import { type ColumnDef } from "@tanstack/react-table";
import { AutoPagination, DataTable, useDataTable } from "@/components/table";
import {
  Badge,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  SearchBar,
  Typography,
} from "@/components/ui";
import useIsMobile from "@/components/ui/sidebar/hooks/use-is-mobile";
import AddUserDialog from "@/features/root/components/add-user-dialog";
import DeleteUserDialog from "@/features/root/components/delete-user-dialog";
import EditUserDialog from "@/features/root/components/edit-user-dialog";
import { useUsersQuery } from "@/features/root/hooks/use-users-query";
import type { User, UserType } from "@/types/root/root.type";

export const Route = createFileRoute("/_protected/root/users")({
  component: RouteComponent,
  validateSearch: (search: Record<string, unknown>) => {
    return {
      customerId: search.customerId as string,
    };
  },
});

const columns: ColumnDef<User>[] = [
  {
    accessorKey: "nickname",
    header: "使用者名稱",
    cell: ({ getValue }) => {
      const nickname = getValue<string | null>();
      return <span className="font-medium">{nickname ?? "-"}</span>;
    },
  },
  {
    accessorKey: "email",
    header: "電子信箱",
    cell: ({ getValue }) => {
      const email = getValue<string>();
      return <span className="font-mono text-sm">{email}</span>;
    },
  },
  {
    accessorKey: "userType",
    header: "權限",
    cell: ({ getValue }) => {
      const userType = getValue<UserType>();
      return (
        <Badge variant={userType === "admin" ? "default" : "secondary"}>
          {userType === "admin" ? "管理員" : "一般使用者"}
        </Badge>
      );
    },
  },
  {
    id: "edit",
    header: "修改",
    cell: ({ row }) => {
      const user = row.original;
      return <EditUserDialog user={user} />;
    },
  },
  {
    id: "delete",
    header: "刪除",
    cell: ({ row }) => {
      const user = row.original;
      return <DeleteUserDialog user={user} />;
    },
  },
];

function RouteComponent() {
  const { customerId } = Route.useSearch();
  const { data: users } = useUsersQuery(customerId);

  const isMobile = useIsMobile();

  const { table, globalFilter, setGlobalFilter } = useDataTable<User>({
    data: users ?? [],
    columns,
    options: {
      paginationOptions: true,
      globalFilterOptions: true,
    },
  });

  return (
    <section className="flex-1">
      <Card className="w-full flex-1">
        <CardHeader>
          <Typography variant="h2">使用者管理</Typography>
          <CardDescription>管理系統使用者，包含基本資訊、權限設定和帳戶狀態</CardDescription>
        </CardHeader>

        <CardContent className="space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <span className="w-full md:w-auto">
              <SearchBar
                className="grow"
                placeholder="請輸入使用者名稱或電子信箱進行搜尋..."
                value={globalFilter}
                onChange={setGlobalFilter}
              />
            </span>

            <AddUserDialog customerId={customerId} />
          </div>

          <DataTable table={table} />

          <AutoPagination table={table} showTotalCount showSizeChanger={!isMobile} />
        </CardContent>
      </Card>
    </section>
  );
}
